// ─── ACCOUNT SETTINGS ─────────────────────────────────────────────────────────
// The settings modal is account management only — email, plan, AI access
// (BYOK key or calls-remaining), subscription portal, and sign out. No external
// API keys. Auth + plan come from the backend via useAuth.

import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { C, s, backendFetch } from "./core";
import { useAuth } from "./AuthContext";

// ─── LOCAL KEY STORE (vestigial; data is backend-served) ──────────────────────
const EMPTY_KEYS = {
  tier:         "free",
  email:        "",
  password:     "",
  anthropicKey: "",
  backendUrl:   "",
};

const STORAGE_KEY = "fterm_keys";

export function loadKeys() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? { ...EMPTY_KEYS, ...JSON.parse(raw) } : null;
  } catch {
    return null;
  }
}

export function saveKeys(keys) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(keys));
}

const TIER_NAMES = {
  trial:         "Trial",
  education:     "Education",
  pro_byok:      "Pro",
  pro_supplied:  "Pro",
  inst_byok:     "Institutional",
  inst_supplied: "Institutional",
  nous_topology: "Nous Topology",
  internal:      "Nous Topology",
  free:          "Free",
};
const BYOK_TIERS = ["education", "pro_byok", "inst_byok"];

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 11, color: C.amber, letterSpacing: "0.12em", fontWeight: 700, marginBottom: 8 }}>
        {title}
      </div>
      {children}
    </div>
  );
}

// ─── SETTINGS MODAL ───────────────────────────────────────────────────────────
export function SettingsModal({ onClose, internal = false }) {
  const { user, tier, me, refreshMe, signOut } = useAuth();
  const [anthropicKey, setAnthropicKey] = useState("");
  const [keyMsg, setKeyMsg] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { refreshMe(); }, [refreshMe]);

  const effectiveTier = internal ? "nous_topology" : (tier || "trial");
  const tierName = TIER_NAMES[effectiveTier] || effectiveTier;
  const isByok = BYOK_TIERS.includes(effectiveTier);

  const trialEnd = me?.trial_end ? new Date(me.trial_end) : null;
  const trialDays = trialEnd
    ? Math.max(0, Math.ceil((trialEnd.getTime() - Date.now()) / 86400000))
    : null;
  const onTrial = trialDays != null &&
    (effectiveTier === "trial" || me?.subscription_status === "trialing");

  const saveKey = async () => {
    setKeyMsg(""); setBusy(true);
    try {
      const res = await backendFetch("https://api", "/api/ai/key", {
        method: "POST",
        body: JSON.stringify({ anthropic_key: anthropicKey.trim() }),
      });
      if (res.ok) { setKeyMsg("Saved."); setAnthropicKey(""); }
      else {
        const e = await res.json().catch(() => ({}));
        setKeyMsg((e.detail && (e.detail.message || e.detail)) || "Could not save key.");
      }
    } catch { setKeyMsg("Could not save key."); }
    setBusy(false);
  };

  const clearKey = async () => {
    setKeyMsg(""); setBusy(true);
    try {
      const res = await backendFetch("https://api", "/api/ai/key", { method: "DELETE" });
      setKeyMsg(res.ok ? "Cleared." : "Could not clear key.");
    } catch { setKeyMsg("Could not clear key."); }
    setBusy(false);
  };

  const openPortal = async () => {
    setBusy(true);
    try {
      const res = await backendFetch("https://api", "/subscriptions/portal", { method: "POST" });
      if (res.ok) {
        const b = await res.json();
        if (b.portal_url) { window.location.href = b.portal_url; return; }
      }
    } catch {}
    setBusy(false);
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.88)", zIndex: 200,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div style={{
        width: 440, maxWidth: "94vw", background: C.panel, border: `1px solid ${C.border}`,
        borderRadius: 4, padding: 20, maxHeight: "90vh", overflowY: "auto",
      }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <span style={{ color: C.amber, fontSize: 14, fontWeight: 700, letterSpacing: "0.15em" }}>
            {internal ? "◈ NT INTERNAL" : "ACCOUNT"}
          </span>
          <button style={{ ...s.btn(), padding: "2px 6px" }} onClick={onClose}>
            <X size={11} />
          </button>
        </div>

        {/* Account */}
        <Section title="ACCOUNT">
          <div style={{ fontSize: 12, color: C.textMid, lineHeight: 2 }}>
            <div>Email: <span style={{ color: C.text }}>{internal ? "internal@nous-topology" : (user?.email || me?.email || "—")}</span></div>
            <div>Plan: <span style={{ color: C.text }}>{tierName}</span></div>
            {onTrial && (
              <div style={{ color: trialDays <= 3 ? "#FF5252" : "#FFD700", fontWeight: 700 }}>
                {trialDays <= 3
                  ? `Trial ends in ${trialDays} day${trialDays !== 1 ? "s" : ""}`
                  : `Trial · ${trialDays} days left`}
              </div>
            )}
          </div>
        </Section>

        {/* AI access */}
        {!internal && (
          <Section title="AI ACCESS">
            {isByok ? (
              <>
                <div style={{ fontSize: 12, color: C.amber, fontWeight: 700, marginBottom: 4 }}>
                  YOUR ANTHROPIC API KEY
                </div>
                <input
                  style={{ ...s.inp, marginBottom: 6 }}
                  type="password"
                  placeholder="sk-ant-..."
                  value={anthropicKey}
                  onChange={(e) => setAnthropicKey(e.target.value)}
                  autoComplete="off"
                  spellCheck={false}
                />
                <div style={{ display: "flex", gap: 6 }}>
                  <button style={s.btn("teal")} disabled={busy || !anthropicKey.trim()} onClick={saveKey}>
                    SAVE KEY
                  </button>
                  <button style={s.btn("red")} disabled={busy} onClick={clearKey}>
                    CLEAR
                  </button>
                </div>
                <div style={{ fontSize: 11, color: C.textDim, marginTop: 6, lineHeight: 1.5 }}>
                  Stored securely on your account. Never returned to the client.
                </div>
                {keyMsg && <div style={{ fontSize: 11, color: C.textMid, marginTop: 4 }}>{keyMsg}</div>}
              </>
            ) : (
              <div style={{ fontSize: 12, color: C.textMid, lineHeight: 1.8 }}>
                {me ? (
                  <>
                    AI calls today: <span style={{ color: C.text }}>{me.ai_calls_used_today ?? 0}</span> used ·{" "}
                    <span style={{ color: C.green }}>
                      {Math.max(0, (me.ai_calls_limit ?? 0) - (me.ai_calls_used_today ?? 0))}
                    </span>{" "}
                    remaining of {me.ai_calls_limit ?? 0}
                  </>
                ) : "AI analyst included with your plan."}
              </div>
            )}
          </Section>
        )}

        {/* Subscription */}
        {!internal && (
          <Section title="SUBSCRIPTION">
            <button style={{ ...s.btn(), width: "100%", padding: "8px 0" }} disabled={busy} onClick={openPortal}>
              MANAGE SUBSCRIPTION
            </button>
            <div style={{ fontSize: 11, color: C.textDim, marginTop: 6, lineHeight: 1.5 }}>
              Billing, payment method, and cancellation are managed in the Stripe customer portal.
            </div>
          </Section>
        )}

        {internal && (
          <div style={{ marginBottom: 16, fontSize: 12, color: C.textMid, lineHeight: 1.7 }}>
            <div style={{ color: C.amber, fontWeight: 700, marginBottom: 4 }}>Nous Topology</div>
            AI analyst active · all features unlocked. No plan restrictions apply.
          </div>
        )}

        {/* Sign out — distinct, not a primary action */}
        <button
          style={{ ...s.btn(), width: "100%", padding: "8px 0", color: "#FF5252", borderColor: "#FF525255" }}
          onClick={async () => { await signOut(); onClose(); }}
        >
          SIGN OUT
        </button>
      </div>
    </div>
  );
}
